import { AwsRum, AwsRumConfig } from "aws-rum-web";

import { Logger } from "@liberty-fund/logger";

/**
 * The RUM settings the browser needs, as published by the CDK deploy.
 *
 * Mirrors `RumClientConfig` in infra/lib/rum-monitor.ts.
 */
interface RumRuntimeConfig {
  applicationId: string;
  applicationVersion: string;
  region: string;
  identityPoolId: string;
  endpoint: string;
  sessionSampleRate: number;
  telemetries: string[];
  allowCookies: boolean;
  enableXRay: boolean;
}

interface RuntimeConfig {
  rum?: RumRuntimeConfig;
}

export let metricsClient: AwsRum | undefined;

/**
 * Starts CloudWatch RUM from configuration published alongside the bundle.
 *
 * The monitor and its Cognito identity pool are created by the CDK app, so
 * their IDs are generated at deploy time — after this bundle was built. They
 * therefore cannot be compiled in the way the rest of the configuration is, and
 * are fetched instead.
 *
 * Telemetry is optional by design: no runtime-config.json (the dev server
 * serves none), a malformed one, or a RUM client that refuses to start must all
 * leave the app running normally.
 */
export const initiatizeMetrics = async (): Promise<void> => {
  if (metricsClient) {
    return;
  }

  try {
    const response = await fetch("/runtime-config.json", { cache: "no-store" });
    if (!response.ok) {
      return;
    }

    const { rum } = (await response.json()) as RuntimeConfig;
    if (!rum?.applicationId || !rum.identityPoolId) {
      return;
    }

    const config: AwsRumConfig = {
      sessionSampleRate: rum.sessionSampleRate,
      identityPoolId: rum.identityPoolId,
      endpoint: rum.endpoint,
      telemetries: rum.telemetries,
      allowCookies: rum.allowCookies,
      enableXRay: rum.enableXRay,
    };

    metricsClient = new AwsRum(
      rum.applicationId,
      rum.applicationVersion,
      rum.region,
      config,
    );
  } catch (error) {
    Logger.debug("CloudWatch RUM not initialised", {
      message: (error as Error)?.message,
    });
  }
};
